import { Button, Typography } from "@mui/joy";
import { useEffect, useMemo, useState } from "react";
import "./InstallPWA.css";

const IOS_STORAGE_PREFIX = "iosModalDismissed";
const FALLBACK_DELAY_MS = 2000;

const normalizePortalName = (value) => {
  return (value || "Portal Pacientes").trim();
};

const getPortalName = () => {
  return normalizePortalName(
    localStorage.getItem("tituloWebPortal") ||
      localStorage.getItem("tituloWeb") ||
      document.title
  );
};

const getPortalStorageKey = (portalName) => {
  const normalizedPortalName = portalName
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "");

  return `${IOS_STORAGE_PREFIX}${normalizedPortalName}`;
};

const isStandaloneMode = () => {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
};

const isSecureInstallContext = () => {
  return window.isSecureContext || window.location.hostname === "localhost";
};

const isLocalhost = () => {
  return (
    window.location.hostname === "localhost" ||
    window.location.hostname === "[::1]" ||
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/.test(
      window.location.hostname
    )
  );
};

const getDeviceInfo = () => {
  const userAgent = window.navigator.userAgent || "";
  const platform = window.navigator.platform || "";
  const maxTouchPoints = window.navigator.maxTouchPoints || 0;
  const isIOS =
    /iPad|iPhone|iPod/.test(userAgent) ||
    (platform === "MacIntel" && maxTouchPoints > 1);
  const isAndroid = /android/i.test(userAgent);
  const isCriOS = /CriOS/i.test(userAgent);
  const isFxiOS = /FxiOS/i.test(userAgent);
  const isEdgiOS = /EdgiOS/i.test(userAgent);
  const isSafari = isIOS && /Safari/i.test(userAgent) && !isCriOS && !isFxiOS && !isEdgiOS;
  const isChromeDesktop = !isIOS && !isAndroid && /Chrome/i.test(userAgent) && !/Edg/i.test(userAgent);

  return {
    isIOS,
    isAndroid,
    isSafari,
    isChromeDesktop,
  };
};

const InstallPWA = () => {
  const portalName = useMemo(() => getPortalName(), []);
  const storageKey = useMemo(() => getPortalStorageKey(portalName), [portalName]);
  const [showModal, setShowModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [mode, setMode] = useState(null);
  const [dismissedInSession, setDismissedInSession] = useState(false);

  useEffect(() => {
    if (isStandaloneMode()) {
      return undefined;
    }

    const { isIOS, isAndroid } = getDeviceInfo();
    const iosDismissed = localStorage.getItem(storageKey) === "true";
    let fallbackTimer;

    const showIOSInstructions = () => {
      if (!iosDismissed) {
        setMode("ios");
        setShowModal(true);
      }
    };

    const showAndroidFallback = () => {
      setMode("android-manual");
      setShowModal(true);
    };

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setMode(isAndroid ? "android-prompt" : "desktop-prompt");
      setShowModal(true);
    };

    const handleAppInstalled = () => {
      setShowModal(false);
      setDeferredPrompt(null);
      setMode(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    if (isIOS) {
      fallbackTimer = window.setTimeout(showIOSInstructions, FALLBACK_DELAY_MS);
    } else if (isAndroid) {
      fallbackTimer = window.setTimeout(() => {
        if (!showModal && !deferredPrompt && !dismissedInSession && !isStandaloneMode()) {
          showAndroidFallback();
        }
      }, FALLBACK_DELAY_MS);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
      if (fallbackTimer) {
        window.clearTimeout(fallbackTimer);
      }
    };
  }, [deferredPrompt, dismissedInSession, storageKey]);

  const handleClose = () => {
    if (mode === "ios") {
      localStorage.setItem(storageKey, "true");
    }

    setDismissedInSession(true);
    setShowModal(false);
  };

  const handleInstall = async () => {
    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowModal(false);
  };

  if (!showModal || isStandaloneMode()) {
    return null;
  }

  const { isIOS, isAndroid, isSafari, isChromeDesktop } = getDeviceInfo();
  const canInstall = Boolean(deferredPrompt);

  return (
    <div className="install-pwa-backdrop" role="presentation">
      <div
        className="install-pwa-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="install-pwa-title"
      >
        <button
          type="button"
          className="install-pwa-close"
          onClick={handleClose}
          aria-label="Cerrar"
        >
          ×
        </button>

        <img
          src="/assets/icon/logo-cuadrado.png"
          alt={`Logo ${portalName}`}
          className="install-pwa-logo"
        />

        <Typography id="install-pwa-title" className="install-pwa-title">
          Crear acceso directo
        </Typography>

        {mode === "ios" && (
          <>
            <Typography className="install-pwa-description">
              Creá un acceso directo a este portal en tu iPhone para entrar más rápido desde tu dispositivo.
            </Typography>
            <ol className="install-pwa-steps">
              <li>Tocá el botón Compartir de {isSafari ? "Safari" : "tu navegador en iOS"}.</li>
              <li>Elegí “Agregar a pantalla de inicio”.</li>
              <li>Tocá “Crear” si tu dispositivo muestra esa opción para confirmar.</li>
            </ol>
          </>
        )}

        {mode === "android-manual" && (
          <>
            <Typography className="install-pwa-description">
              Si tu navegador no muestra el instalador automático, podés crear el acceso directo manualmente.
            </Typography>
            <ol className="install-pwa-steps">
              <li>Abrí el menú de tres puntos del navegador.</li>
              <li>Elegí “Instalar app” o “Agregar a pantalla principal”, según la opción disponible.</li>
              <li>Confirmá la creación del acceso directo.</li>
            </ol>
          </>
        )}

        {(mode === "android-prompt" || mode === "desktop-prompt") && (
          <Typography className="install-pwa-description">
            {isAndroid
              ? "Tu navegador detectó que podés crear un acceso directo a este portal."
              : isChromeDesktop
                ? "Podés crear un acceso directo a este portal en Chrome para entrar más rápido desde tu dispositivo."
                : "Podés crear un acceso directo a este portal desde tu navegador."}
          </Typography>
        )}

        <div className="install-pwa-actions">
          {canInstall && (
            <Button className="install-pwa-primary" onClick={handleInstall}>
              Crear acceso directo
            </Button>
          )}
          <Button variant="plain" color="neutral" onClick={handleClose}>
            Ahora no
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InstallPWA;
